import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const fmtData = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';

function giorniAllaScadenza(data) {
  return Math.ceil((new Date(data) - new Date()) / (1000 * 60 * 60 * 24));
}

function BadgeScadenza({ data }) {
  const giorni = giorniAllaScadenza(data);
  if (giorni < 0)    return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Scaduta da {Math.abs(giorni)}gg</span>;
  if (giorni <= 60)  return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">Scade tra {giorni}gg</span>;
  return               <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">Scade tra {giorni}gg</span>;
}

function TabellaScadenze({ lista, titolo, colore }) {
  const navigate = useNavigate();
  if (lista.length === 0) return null;
  return (
    <div className="bg-white rounded-xl shadow overflow-hidden">
      <div className={`px-5 py-3 flex items-center gap-2 ${colore}`}>
        <span className="font-bold text-sm">{titolo}</span>
        <span className="ml-auto text-xs font-semibold bg-white bg-opacity-30 px-2 py-0.5 rounded-full">{lista.length}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs font-semibold">
            <tr>
              <th className="text-left px-4 py-2">Ragione sociale</th>
              <th className="text-left px-4 py-2">N° Attestazione</th>
              <th className="text-left px-4 py-2">Tipo</th>
              <th className="text-left px-4 py-2">Scadenza</th>
              <th className="text-left px-4 py-2">Stato</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {lista.map((imp, i) => (
              <tr key={imp.id} className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 cursor-pointer transition`}
                onClick={() => navigate(`/admin/imprese-soa/${imp.id}`)}>
                <td className="px-4 py-2.5 font-medium text-[#1A3A5C]">{imp.ragione_sociale}</td>
                <td className="px-4 py-2.5 text-gray-600">{imp.att_numero || '—'}</td>
                <td className="px-4 py-2.5 text-gray-600 capitalize">{imp.att_tipo_scadenza || '—'}</td>
                <td className="px-4 py-2.5 text-gray-600">{fmtData(imp.att_data_scadenza)}</td>
                <td className="px-4 py-2.5"><BadgeScadenza data={imp.att_data_scadenza} /></td>
                <td className="px-4 py-2.5 text-right text-[#2E6096] text-xs hover:underline">Apri →</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invio, setInvio] = useState({ stato: null, msg: '' });
  const [backup, setBackup] = useState({ stato: null, msg: '' });

  useEffect(() => {
    axios.get('/api/notifiche/scadenze-soa')
      .then(r => setDati(r.data))
      .finally(() => setLoading(false));
  }, []);

  async function inviaEmail() {
    setInvio({ stato: 'loading', msg: '' });
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post('/api/notifiche/invia-scadenze', {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data.inviata) {
        setInvio({ stato: 'ok', msg: `Email inviata (${r.data.scadute} scadute, ${r.data.inScadenza} in scadenza)` });
      } else {
        setInvio({ stato: 'ok', msg: r.data.motivo || 'Nessuna scadenza imminente' });
      }
    } catch {
      setInvio({ stato: 'error', msg: 'Errore nell\'invio. Controlla le variabili d\'ambiente.' });
    }
  }

  async function scaricaBackup() {
    setBackup({ stato: 'loading', msg: '' });
    try {
      const r = await axios.get('/api/backup/scarica', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/gzip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup_cqs_${new Date().toISOString().slice(0, 10)}.json.gz`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setBackup({ stato: 'ok', msg: 'Backup scaricato con successo' });
    } catch {
      setBackup({ stato: 'error', msg: 'Errore nel download del backup' });
    }
  }

  const totaleAllerta = dati ? dati.scadute.length + dati.inScadenza.length : 0;

  return (
    <div className="space-y-6">
      {/* Intestazione */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1A3A5C]">Dashboard</h1>
          {!loading && totaleAllerta > 0 && (
            <p className="text-sm text-gray-500 mt-0.5">
              {totaleAllerta} attestazion{totaleAllerta === 1 ? 'e' : 'i'} che richied{totaleAllerta === 1 ? 'e' : 'ono'} attenzione
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={inviaEmail}
            disabled={invio.stato === 'loading'}
            className="bg-[#1A3A5C] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#2E6096] transition disabled:opacity-50"
          >
            {invio.stato === 'loading' ? 'Invio...' : '✉ Invia email scadenze'}
          </button>
          <button
            onClick={scaricaBackup}
            disabled={backup.stato === 'loading'}
            className="bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition disabled:opacity-50"
          >
            {backup.stato === 'loading' ? 'Preparazione…' : '⬇ Scarica backup'}
          </button>
        </div>
      </div>

      {invio.stato && invio.stato !== 'loading' && (
        <div className={`rounded-lg px-4 py-3 text-sm font-medium ${invio.stato === 'ok' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {invio.msg}
        </div>
      )}
      {backup.stato && backup.stato !== 'loading' && (
        <div className={`rounded-lg px-4 py-3 text-sm font-medium ${backup.stato === 'ok' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {backup.msg}
        </div>
      )}

      {loading && <p className="text-gray-400 text-sm">Caricamento...</p>}

      {!loading && dati && (
        <>
          {totaleAllerta === 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-4 text-green-700 font-medium text-sm">
              Tutte le attestazioni SOA sono valide. Nessuna scadenza imminente.
            </div>
          )}
          <TabellaScadenze
            lista={dati.scadute}
            titolo="Attestazioni scadute"
            colore="bg-red-600 text-white"
          />
          <TabellaScadenze
            lista={dati.inScadenza}
            titolo="In scadenza entro 180 giorni"
            colore="bg-yellow-500 text-white"
          />
        </>
      )}
    </div>
  );
}
